import { Flex, FlexProps } from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import { useCallback, useEffect, useRef, useState } from 'react';

import { LDFlags } from '../constants/flags';
import { EmployerPhoneLookupForm } from '../gql/generated/graphql';
import { useTempInfo } from '../hooks';
import useEmployerPhoneLookup, { EmployerPhoneLookupResult } from '../hooks/useEmployerPhoneLookup';
import useFlag from '../hooks/useFlag';
import { formatPhoneInput } from '../utils/strings';
import Input from './formComponents/Input';

export type EmployerLookupHandle = {
  finalize: () => Promise<void>;
};

type RegisterLookup = (handle: EmployerLookupHandle) => () => void;

interface Props extends FlexProps {
  form: EmployerPhoneLookupForm;
  nameField: string;
  phoneField: string;
  phoneMask?: string;
  registerLookup?: RegisterLookup;
}

const isPreviousForm = (form: EmployerPhoneLookupForm): boolean =>
  form === EmployerPhoneLookupForm.BuyerPrevious ||
  form === EmployerPhoneLookupForm.CobuyerPrevious;

const isCobuyerForm = (form: EmployerPhoneLookupForm): boolean =>
  form === EmployerPhoneLookupForm.CobuyerCurrent ||
  form === EmployerPhoneLookupForm.CobuyerPrevious;

const EmployerNameAndPhoneInputs = ({
  form,
  nameField,
  phoneField,
  phoneMask = '(999) 999-9999',
  registerLookup,
  ...flexProps
}: Props) => {
  const { info, refetch } = useTempInfo({ fetchPolicy: 'network-only' });
  const tempInfoData = info?.data;

  const labelPrefix = isPreviousForm(form) ? 'PREVIOUS ' : '';
  const nameLabel = `${labelPrefix}EMPLOYER NAME`;
  const phoneLabel = `${labelPrefix}EMPLOYER PHONE`;

  const isCobuyer = isCobuyerForm(form);
  const userFirstName = isCobuyer
    ? tempInfoData?.cobuyer_first_name ?? ''
    : tempInfoData?.first_name ?? '';
  const userLastName = isCobuyer
    ? tempInfoData?.cobuyer_last_name ?? ''
    : tempInfoData?.last_name ?? '';
  const userPhone = isCobuyer
    ? tempInfoData?.cobuyer_phone_number ?? ''
    : tempInfoData?.phone_number ?? '';
  const city = tempInfoData?.city ?? '';
  const state = tempInfoData?.state ?? '';
  const zip = String(tempInfoData?.zip ?? '');
  const temporaryInfoId = info?.id ?? '';

  const { getFieldMeta, setFieldValue } = useFormikContext<Record<string, string>>();
  const { lookup } = useEmployerPhoneLookup();
  const flagOn = !!useFlag(LDFlags.AUTO_POPULATE_EMPLOYER_PHONE);

  const [revealed, setRevealed] = useState<boolean>(!flagOn);
  const inflightPromiseRef = useRef<Promise<EmployerPhoneLookupResult> | null>(null);
  const lastQueriedNameRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const applyResult = useCallback(
    async ({ phoneNumber, isSelfEmployed }: EmployerPhoneLookupResult) => {
      if (isSelfEmployed) {
        let phone = userPhone;
        if (!phone) {
          const freshTempInfoData = (await refetch())?.data;
          phone =
            (isCobuyer
              ? freshTempInfoData?.cobuyer_phone_number
              : freshTempInfoData?.phone_number) ?? '';
        }
        if (phone) {
          setFieldValue(phoneField, formatPhoneInput(phone, phoneMask));
          setRevealed(false);
        } else {
          setRevealed(true);
        }
      } else if (phoneNumber) {
        setFieldValue(phoneField, formatPhoneInput(phoneNumber, phoneMask));
        setRevealed(false);
        refetch();
      } else {
        setFieldValue(phoneField, '');
        setRevealed(true);
      }
    },
    [phoneField, phoneMask, setFieldValue, userPhone, isCobuyer, refetch],
  );

  const onEmployerNameBlur = useCallback(
    async (rawEmployerName: string) => {
      if (!flagOn) return;
      const employerName = rawEmployerName?.trim();
      if (!employerName) return;
      if (!temporaryInfoId) return;

      // Skip when the name is unchanged from what we already have a number for —
      // either a name queried this session, or a saved name with a saved phone.
      const savedName = String(getFieldMeta(nameField).initialValue ?? '').trim();
      const hasSavedPhone = !!getFieldMeta(phoneField).initialValue;
      const resolvedName = lastQueriedNameRef.current ?? (hasSavedPhone ? savedName : null);
      if (resolvedName === employerName) return;

      lastQueriedNameRef.current = employerName;

      const promise = lookup({
        employerName,
        city,
        state,
        zip,
        userFirstName,
        userLastName,
        userPhone,
        temporaryInfoId,
        form,
      });
      inflightPromiseRef.current = promise;

      const result = await promise;
      if (inflightPromiseRef.current === promise) {
        inflightPromiseRef.current = null;
      }

      await applyResult(result);
    },
    [
      applyResult,
      city,
      flagOn,
      form,
      getFieldMeta,
      lookup,
      nameField,
      phoneField,
      state,
      temporaryInfoId,
      userFirstName,
      userLastName,
      userPhone,
      zip,
    ],
  );

  const finalize = useCallback(async () => {
    const promise = inflightPromiseRef.current;
    if (!promise) return;
    const result = await promise;
    await applyResult(result);
  }, [applyResult]);

  useEffect(() => {
    if (!registerLookup) return undefined;
    return registerLookup({ finalize });
  }, [registerLookup, finalize]);

  return (
    <Flex ref={containerRef} flexDir={{ base: 'column', md: 'row' }} {...flexProps}>
      <Input
        label={nameLabel}
        name={nameField}
        _container={{
          mr: { base: 0, md: revealed ? 2 : 0 },
          mb: { base: revealed ? 4 : 0, md: 0 },
          flex: revealed ? undefined : 1,
        }}
        maxLength={50}
        onBlur={onEmployerNameBlur}
      />
      {revealed && (
        <Input
          label={phoneLabel}
          name={phoneField}
          mask={phoneMask}
          type="tel"
          _container={{ ml: { base: 0, md: 2 } }}
        />
      )}
    </Flex>
  );
};

export default EmployerNameAndPhoneInputs;
